import { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';

const DEFAULT_NEUTRAL_RGBA = [210, 210, 210, 255];
const textureCache = new Map();
const loadingPromises = new Map(); // cacheKey → Promise<THREE.Texture> (deduplicates concurrent loads)
const textureCacheOrder = [];
const MAX_CACHED_TEXTURES = 32;

function getWebpCandidate(url) {
  if (!url) return null;
  if (/\.webp(\?.*)?$/i.test(url)) return url;
  const match = url.match(/\.(jpg|jpeg|png)(\?.*)?$/i);
  if (!match) return null;
  return url.replace(match[0], `.webp${match[2] || ''}`);
}

function touchCacheKey(key) {
  const idx = textureCacheOrder.indexOf(key);
  if (idx >= 0) textureCacheOrder.splice(idx, 1);
  textureCacheOrder.push(key);
}

function putTextureInCache(key, tex) {
  textureCache.set(key, tex);
  touchCacheKey(key);
  while (textureCacheOrder.length > MAX_CACHED_TEXTURES) {
    const oldest = textureCacheOrder.shift();
    if (!oldest) break;
    const oldTex = textureCache.get(oldest);
    textureCache.delete(oldest);
    oldTex?.dispose?.();
  }
}

function getCachedStaticTexture(resolved) {
  if (!resolved) return null;
  return textureCache.get(`static:${resolved}`) ?? null;
}

function loadTexturePromise(url) {
  const loader = new THREE.TextureLoader();
  return new Promise((resolve, reject) => {
    loader.load(url, resolve, undefined, reject);
  });
}

export function useOptimizedTexture(url, fallbackUrl = null) {
  const { gl } = useThree();
  const resolved = url || fallbackUrl || '';
  const [texture, setTexture] = useState(() => getCachedStaticTexture(resolved));

  const neutral = useMemo(() => {
    const data = new Uint8Array(DEFAULT_NEUTRAL_RGBA);
    const t = new THREE.DataTexture(data, 1, 1);
    t.needsUpdate = true;
    t.wrapS = THREE.RepeatWrapping;
    t.wrapT = THREE.RepeatWrapping;
    return t;
  }, []);

  const targetAnisotropy = useMemo(() => {
    const maxSupported = gl?.capabilities?.getMaxAnisotropy?.() ?? 4;
    return Math.max(1, Math.min(maxSupported, 8));
  }, [gl]);

  useEffect(() => {
    if (!resolved) return undefined;

    let cancelled = false;
    const cacheKey = `static:${resolved}`;

    const applyAndCommit = (tex) => {
      if (cancelled) { tex.dispose?.(); return; }
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = targetAnisotropy;
      tex.needsUpdate = true;
      putTextureInCache(cacheKey, tex);
      setTexture(tex);
    };

    // 1. Already in memory cache
    const cached = textureCache.get(cacheKey);
    if (cached) {
      touchCacheKey(cacheKey);
      applyAndCommit(cached);
      return () => { cancelled = true; };
    }

    // 2. Already loading — attach to existing Promise (prevents duplicate network request)
    const existing = loadingPromises.get(cacheKey);
    if (existing) {
      existing.then(tex => { if (!cancelled) applyAndCommit(tex); }).catch(() => {});
      return () => { cancelled = true; };
    }

    // 3. Start new load — try WebP first, fall back to original
    const webpCandidate = getWebpCandidate(resolved);
    const loadPromise = (webpCandidate && webpCandidate !== resolved)
      ? loadTexturePromise(webpCandidate).catch(() => loadTexturePromise(resolved))
      : loadTexturePromise(resolved);

    loadingPromises.set(cacheKey, loadPromise);

    loadPromise
      .then(tex => {
        loadingPromises.delete(cacheKey);
        if (!cancelled) applyAndCommit(tex);
      })
      .catch(() => {
        loadingPromises.delete(cacheKey);
      });

    return () => { cancelled = true; };
  }, [resolved, targetAnisotropy]);

  useEffect(() => () => setTexture(null), []);

  return texture ?? neutral;
}
